import type { UIMessage } from "@ai-sdk/react";
import * as z from "zod";

export const ChatMessageSource = {
  Typed: "typed",
  Suggestion: "suggestion",
} as const;

export type ChatMessageSource =
  (typeof ChatMessageSource)[keyof typeof ChatMessageSource];

/** Matches the nav rail, which the panel sits over. */
export const CHAT_MIN_WIDTH = 296;

export const CHAT_DEFAULT_WIDTH = 420;

export const CHAT_MAX_WIDTH = 720;

/** Width the content column is never squeezed below, whatever the panel asks for. */
export const CHAT_CONTENT_MIN_WIDTH = 480;

export const clampChatWidth = (width: number, viewportWidth: number) =>
  Math.round(
    Math.min(
      Math.max(width, CHAT_MIN_WIDTH),
      CHAT_MAX_WIDTH,
      Math.max(viewportWidth - CHAT_CONTENT_MIN_WIDTH, CHAT_MIN_WIDTH),
    ),
  );

const CHAT_MESSAGE_SOURCE_HEADER = "X-Chat-Message-Source";

export const suggestionMessageSendOptions = {
  headers: {
    [CHAT_MESSAGE_SOURCE_HEADER]: ChatMessageSource.Suggestion,
  },
} as const;

const CHAT_SUGGESTIONS: string[] = [
  "What's important in a plan offer?",
  "How can we compare customer bills?",
  "How can I speed up enrollment?",
  "When is a renewal offer sent?",
  "How are plan prices determined?",
  "Who fixes area power outages?",
  "What technical integrations are available?",
  "Can I subsidize a plan?",
  "What are the possible account statuses?",
];

// Returns random suggestions from the above list, up to the number specified.
export const getChatSuggestions = (num: number) => {
  const shuffled = [...CHAT_SUGGESTIONS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, num);
};

const DASHBOARD_ORIGIN = "https://dashboard.light.dev";

/** Determine which URLs sent in chat can be treated as relative. */
export const parseRelativeHref = (href: string) => {
  if (href.startsWith("/")) {
    return { href, isRelative: true };
  }
  if (href.startsWith(DASHBOARD_ORIGIN)) {
    return {
      href: href.slice(DASHBOARD_ORIGIN.length) || "/",
      isRelative: true,
    };
  }
  return { href, isRelative: false };
};

export const FinalResultInput = z.object({
  text: z.string().default(""),
  links: z
    .array(
      z.object({
        url: z.string().default(""),
        title: z.string().default(""),
      }),
    )
    .default([]),
});

export const getMessagePartsTexts = (
  message: Pick<UIMessage, "parts">,
): string[] => {
  return message.parts.map((part) => {
    if (part.type === "text") return part.text;
    if (part.type === "tool-final_result") {
      return (part.input as z.infer<typeof FinalResultInput>).text || "";
    }
    return "";
  });
};
